import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const root = window.Shopify?.routes?.root || '/';
let currentCart = null;
let refreshing = null;
let busyVariant = null;
const pending = new Set();
const checkoutLocks = new Set();
const elements = () => [...document.querySelectorAll('dl-upsells')];

function render() {
  for (const group of elements()) {
    for (const card of group.querySelectorAll('[data-upsell-variant]')) {
      const selected = currentCart
        ? currentCart.items.some(item => String(item.variant_id) === card.dataset.upsellVariant)
        : card.dataset.selected === 'true';
      card.dataset.selected = String(selected);
      card.querySelector('.dlu-selected').hidden = !selected;
      const button = card.querySelector('[data-upsell-toggle]');
      button.setAttribute('aria-pressed', String(selected));
      button.setAttribute('aria-label', selected ? button.dataset.removeLabel : button.dataset.addLabel);
      button.disabled = busyVariant !== null || pending.size > 0 || !currentCart;
      button.textContent = busyVariant === card.dataset.upsellVariant
        ? group.dataset.updating : selected ? group.dataset.remove : group.dataset.add;
    }
    group.setAttribute('aria-busy', String(busyVariant !== null));
  }
  if (busyVariant !== null) {
    for (const button of document.querySelectorAll('button[name="checkout"],input[name="checkout"]')) {
      if (!button.disabled) { checkoutLocks.add(button); button.disabled = true; }
    }
  } else {
    for (const button of checkoutLocks) button.disabled = false;
    checkoutLocks.clear();
  }
}

async function readCart() {
  const response = await fetch(`${root}cart.js`, { credentials: 'same-origin', cache: 'no-store' });
  if (!response.ok) throw new Error('Cart unavailable');
  return response.json();
}

function refresh() {
  if (refreshing) return refreshing;
  refreshing = readCart().then(cart => { currentCart = cart; render(); return cart; })
    .finally(() => { refreshing = null; });
  return refreshing;
}

function message(text, error = false) {
  for (const group of elements()) {
    const status = group.querySelector('.dlu-status');
    status.textContent = text;
    status.dataset.error = String(error);
  }
}

document.addEventListener(StandardEvents.cartLinesUpdate, event => {
  if (!event.promise) { refresh().catch(() => {}); return; }
  const promise = event.promise;
  pending.add(promise);
  render();
  promise.then(({ detail }) => {
    if (Array.isArray(detail?.items)) {
      currentCart = { items: detail.items };
    } else {
      refresh().catch(() => {});
    }
  }).catch(() => { refresh().catch(() => {}); }).finally(() => {
    pending.delete(promise);
    render();
    requestAnimationFrame(render);
  });
});

async function toggle(group, variantId, remove) {
  if (busyVariant !== null || pending.size) return;
  busyVariant = variantId;
  message('');
  render();
  let deferred;
  try {
    if (refreshing) await refreshing;
    currentCart = await readCart();
    const matches = currentCart.items.filter(item => String(item.variant_id) === variantId);
    if (remove ? !matches.length : matches.length > 0) return;
    const sections = [...new Set([...document.querySelectorAll('cart-items-component')]
      .map(component => component.dataset.sectionId).filter(Boolean))].slice(0, 5);
    deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(new CartLinesUpdateEvent({
      action: remove ? 'remove' : 'add',
      context: group.dataset.context === 'product' ? 'product' : 'cart',
      lines: remove ? matches.map(item => ({ id: item.key, quantity: 0 }))
        : [{ merchandiseId: variantId, quantity: 1 }],
      promise: deferred.promise,
    }));
    const payload = remove
      ? { updates: Object.fromEntries(matches.map(item => [item.key, 0])) }
      : { items: [{ id: Number(variantId), quantity: 1 }] };
    payload.sections = sections.join(',');
    payload.sections_url = window.location.pathname;
    const response = await fetch(`${root}cart/${remove ? 'update' : 'add'}.js`, {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });
    const result = await response.json();
    if (!response.ok || result.status >= 400) throw new Error(result.description || 'Cart update failed');
    currentCart = await readCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(currentCart),
      detail: { items: currentCart.items, sections: result.sections, itemCount: currentCart.item_count,
        source: 'dl-upsells', didError: false },
    });
    message(remove ? group.dataset.removedMessage : group.dataset.addedMessage);
  } catch (error) {
    try { currentCart = await readCart(); } catch (_) {}
    if (deferred) {
      if (currentCart) deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(currentCart),
        detail: { items: currentCart.items, source: 'dl-upsells', didError: true },
      });
      else deferred.reject(error);
    }
    message(group.dataset.error, true);
  } finally {
    busyVariant = null;
    render();
    requestAnimationFrame(render);
  }
}

class DigitalLibraryUpsells extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.onClick);
    render();
    if (!currentCart) refresh().catch(() => { message(this.dataset.error, true); });
  }
  disconnectedCallback() { this.removeEventListener('click', this.onClick); }
  onClick = event => {
    const button = event.target.closest('[data-upsell-toggle]');
    if (!button || !this.contains(button) || button.disabled) return;
    const card = button.closest('[data-upsell-variant]');
    toggle(this, card.dataset.upsellVariant, card.dataset.selected === 'true');
  };
}
if (!customElements.get('dl-upsells')) customElements.define('dl-upsells', DigitalLibraryUpsells);
window.addEventListener('pageshow', () => { if (elements().length) refresh().catch(() => {}); });
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible' && elements().length && !busyVariant) refresh().catch(() => {});
});
